import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CalibrationScheduleCalendar = ({ equipment, onScheduleCalibration }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0)?.getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1)?.getDay();
  };

  const getCalibrationEvents = useMemo(() => {
    const events = [];
    equipment?.forEach(item => {
      const dueDate = new Date(item.nextDueDate);
      const today = new Date();
      const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
      
      events?.push({
        id: item?.id,
        date: dueDate,
        title: `${item?.name} Calibration`,
        deviceId: item?.deviceId,
        department: item?.department,
        priority: item?.priority,
        status: diffDays <= 0 ? 'overdue' : diffDays <= 7 ? 'urgent' : diffDays <= 30 ? 'upcoming' : 'scheduled',
        type: 'calibration'
      });

      // Add maintenance events
      const maintenanceDate = new Date(item.lastMaintenance);
      maintenanceDate?.setMonth(maintenanceDate?.getMonth() + 3); // Next maintenance in 3 months
      
      events?.push({
        id: `${item?.id}_maintenance`,
        date: maintenanceDate,
        title: `${item?.name} Maintenance`,
        deviceId: item?.deviceId,
        department: item?.department,
        priority: 'medium',
        status: 'scheduled',
        type: 'maintenance'
      });
    });

    return events;
  }, [equipment]);

  const getEventsByDate = (date) => {
    return getCalibrationEvents?.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate?.toDateString() === date?.toDateString();
    });
  };

  const getEventStatusColor = (status) => {
    switch (status) {
      case 'overdue':
        return 'bg-error text-error-foreground';
      case 'urgent':
        return 'bg-warning text-warning-foreground';
      case 'upcoming':
        return 'bg-primary text-primary-foreground';
      case 'scheduled':
        return 'bg-secondary text-secondary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate?.setMonth(newDate?.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const renderCalendarGrid = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const today = new Date();

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days?.push(
        <div key={`empty-${i}`} className="h-24 border border-border bg-muted/20"></div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const events = getEventsByDate(date);
      const isToday = date?.toDateString() === today?.toDateString();

      days?.push(
        <div
          key={day}
          className={`h-24 border border-border bg-card hover:bg-muted/30 transition-colors duration-150 p-1 ${
            isToday ? 'ring-2 ring-primary' : ''
          }`}
        >
          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-primary' : 'text-foreground'}`}>
            {day}
          </div>
          <div className="space-y-1 overflow-hidden">
            {events?.slice(0, 2)?.map((event, index) => (
              <div
                key={event?.id}
                className={`text-xs px-1 py-0.5 rounded truncate ${getEventStatusColor(event?.status)}`}
                title={`${event?.title} - ${event?.deviceId}`}
              >
                {event?.type === 'calibration' ? '🔧' : '⚙️'} {event?.deviceId}
              </div>
            ))}
            {events?.length > 2 && (
              <div className="text-xs text-muted-foreground px-1">
                +{events?.length - 2} more
              </div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  const getUpcomingEvents = () => {
    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    return getCalibrationEvents?.filter(event => event?.date >= today && event?.date <= nextWeek)?.sort((a, b) => a?.date - b?.date)?.slice(0, 5);
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Calibration Schedule</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Equipment calibration and maintenance calendar
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateMonth(-1)}
                iconName="ChevronLeft"
                iconSize={16}
              />
              <span className="text-lg font-medium text-foreground min-w-[140px] text-center">
                {currentDate?.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateMonth(1)}
                iconName="ChevronRight"
                iconSize={16}
              />
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={onScheduleCalibration}
              iconName="Plus"
              iconSize={16}
            >
              Schedule
            </Button>
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Calendar Grid */}
          <div className="xl:col-span-3">
            {/* Calendar Header */}
            <div className="grid grid-cols-7 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map(day => (
                <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Body */}
            <div className="grid grid-cols-7 gap-0 border border-border rounded-lg overflow-hidden">
              {renderCalendarGrid()}
            </div>
            
            {/* Legend */}
            <div className="flex items-center justify-center space-x-6 mt-4 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-error rounded"></div>
                <span className="text-muted-foreground">Overdue</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-warning rounded"></div>
                <span className="text-muted-foreground">Urgent (≤7 days)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-primary rounded"></div>
                <span className="text-muted-foreground">Upcoming (≤30 days)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-secondary rounded"></div>
                <span className="text-muted-foreground">Scheduled</span>
              </div>
            </div>
          </div>

          {/* Upcoming Events Sidebar */}
          <div className="xl:col-span-1">
            <div className="bg-muted/20 rounded-lg p-4">
              <h3 className="font-medium text-foreground mb-4 flex items-center">
                <Icon name="Clock" size={16} className="mr-2" />
                Next 7 Days
              </h3>
              
              <div className="space-y-3">
                {getUpcomingEvents()?.map(event => (
                  <div key={event?.id} className="bg-card rounded-lg p-3 border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${getEventStatusColor(event?.status)}`}>
                        {event?.status?.toUpperCase()}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {event?.date?.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    
                    <h4 className="text-sm font-medium text-foreground mb-1">
                      {event?.deviceId}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {event?.department} • {event?.type}
                    </p>
                  </div>
                ))}
                
                {getUpcomingEvents()?.length === 0 && (
                  <div className="text-center py-8">
                    <Icon name="Calendar" size={32} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No events in next 7 days</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalibrationScheduleCalendar;